<template>
    <div class="flex flex-col gap-1">
        <BaseTextarea
            :id="id"
            v-model:input="localValue"
            :maxlength="maxlength"
            :placeholder="placeholder"
            :aria-label="ariaLabel"
            rows="4"
            class="bg-control-bg border border-control-border rounded-lg p-3 body-small text-control-text focus-field"
            @blur="saveIfChanged" />
        <div class="flex justify-between details text-ink-muted">
            <Transition name="fade">
                <span v-if="isShowing" :key="statusText">{{ statusText }}</span>
            </Transition>
            <output :for="id" aria-label="Character count">{{ characterCount }}/{{ maxlength }}</output>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { computed, ref, watch } from "vue";

const props = withDefaults(
    defineProps<{
        id: string;
        ariaLabel: string;
        modelValue?: string | null;
        maxlength?: number;
        placeholder?: string;
    }>(),
    {
        maxlength: 1500,
        placeholder: "",
    },
);

const emit = defineEmits<{ save: [value: string] }>();

const localValue = ref(props.modelValue ?? "");
const lastSavedValue = ref(props.modelValue ?? "");
const statusText = ref("");
const { isShowing, show: showStatus } = useTimedShow({ duration: 2000 });

watch(
    () => props.modelValue,
    (newVal) => {
        const normalized = newVal ?? "";
        localValue.value = normalized;
        lastSavedValue.value = normalized;
    },
);

const characterCount = computed(() => localValue.value.length);

function saveIfChanged() {
    if (localValue.value === lastSavedValue.value) {
        return;
    }

    emit("save", localValue.value);
    lastSavedValue.value = localValue.value;
    statusText.value = "Saved";
    showStatus();
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
